import { CorruptedDatabaseFileException } from '@pkg/basic'
import type { DatabaseWithTransaction } from '@pkg/database-portfolio'
import { sql } from 'drizzle-orm'
import { z } from 'zod'

/** SQLiteのEXISTS結果を検証するschema。 */
const existsRowsSchema = z.array(
  z.tuple([z.union([z.literal(0), z.literal(1)])]),
)

/** SQLiteの文字列1列の結果を検証するschema。 */
const textRowsSchema = z.array(z.tuple([z.string()]))

type LegacyMigrationSignature = {
  readonly id: number
  readonly exists: (params: {
    readonly database: DatabaseWithTransaction['database']
  }) => Promise<boolean>
}

/**
 * SQLiteのEXISTS結果をbooleanへ変換する。
 * @param rows databaseから返された未検証の行。
 * @returns 対象が存在する場合はtrue。
 */
const parseExists = ({ rows }: { readonly rows: unknown }): boolean =>
  existsRowsSchema.parse(rows)[0]?.[0] === 1

/**
 * sqlite_masterにtableが存在するか確認する。
 * @param database migration対象のDB接続。
 * @param tableName 確認するtable名。
 * @returns tableが存在する場合はtrue。
 */
const tableExists = async ({
  database,
  tableName,
}: {
  readonly database: DatabaseWithTransaction['database']
  readonly tableName: string
}): Promise<boolean> =>
  parseExists({
    rows: await database.values(sql`
      SELECT EXISTS(
        SELECT 1
        FROM sqlite_master
        WHERE type = 'table' AND name = ${tableName}
      );
    `),
  })

/**
 * tableにcolumnが存在するか確認する。
 * @param database migration対象のDB接続。
 * @param tableName 確認するtable名。
 * @param columnName 確認するcolumn名。
 * @returns columnが存在する場合はtrue。
 */
const columnExists = async ({
  database,
  tableName,
  columnName,
}: {
  readonly database: DatabaseWithTransaction['database']
  readonly tableName: string
  readonly columnName: string
}): Promise<boolean> =>
  parseExists({
    rows: await database.values(sql`
      SELECT EXISTS(
        SELECT 1
        FROM pragma_table_info(${tableName})
        WHERE name = ${columnName}
      );
    `),
  })

/**
 * commentCandidate.partsが0007適用後のTEXT型か確認する。
 * @param database migration対象のDB接続。
 * @returns 宣言型がTEXTの場合はtrue。
 */
const hasTextCommentCandidateParts = async ({
  database,
}: {
  readonly database: DatabaseWithTransaction['database']
}): Promise<boolean> =>
  parseExists({
    rows: await database.values(sql`
      SELECT EXISTS(
        SELECT 1
        FROM pragma_table_info('commentCandidate')
        WHERE name = 'parts' AND upper(type) = 'TEXT'
      );
    `),
  })

/**
 * accountApiが0013Patch1適用後の外部キー構成か確認する。
 * @param database migration対象のDB接続。
 * @returns accountだけを参照する現在の外部キー構成の場合はtrue。
 */
const hasPatchedAccountApiForeignKeys = async ({
  database,
}: {
  readonly database: DatabaseWithTransaction['database']
}): Promise<boolean> => {
  const rows = textRowsSchema.parse(
    await database.values(sql`
      SELECT DISTINCT "table"
      FROM pragma_foreign_key_list('accountApi')
      ORDER BY "table";
    `),
  )
  return rows.length === 1 && rows[0]?.[0] === 'account'
}

/** 0000から0013までの順序付きschema signature。 */
const legacyMigrationSignatures: readonly LegacyMigrationSignature[] = [
  {
    id: 0,
    exists: async ({ database }) => {
      const results = await Promise.all([
        tableExists({ database, tableName: 'portfolio' }),
        tableExists({ database, tableName: 'actionSource' }),
        tableExists({ database, tableName: 'action' }),
      ])
      return results.every(Boolean)
    },
  },
  {
    id: 1,
    exists: async ({ database }) =>
      await tableExists({ database, tableName: 'polkadotExtrinsic' }),
  },
  {
    id: 2,
    exists: async ({ database }) =>
      await tableExists({ database, tableName: 'sync' }),
  },
  {
    id: 3,
    exists: async ({ database }) =>
      await tableExists({ database, tableName: 'accountApi' }),
  },
  {
    id: 4,
    exists: async ({ database }) =>
      await tableExists({ database, tableName: 'actionEditRule' }),
  },
  {
    id: 5,
    exists: async ({ database }) =>
      await columnExists({
        database,
        tableName: 'actionEditRule',
        columnName: 'stopped',
      }),
  },
  {
    id: 6,
    exists: async ({ database }) =>
      await tableExists({ database, tableName: 'commentCandidate' }),
  },
  {
    id: 7,
    exists: hasTextCommentCandidateParts,
  },
  {
    id: 8,
    exists: async ({ database }) =>
      await tableExists({ database, tableName: 'customActionSubType' }),
  },
  {
    id: 9,
    exists: async ({ database }) =>
      await tableExists({ database, tableName: 'nftAcquiredTimestamp' }),
  },
  {
    id: 10,
    exists: async ({ database }) =>
      await columnExists({
        database,
        tableName: 'commentCandidate',
        columnName: 'displayOrder',
      }),
  },
  {
    id: 11,
    exists: async ({ database }) =>
      await columnExists({
        database,
        tableName: 'action',
        columnName: 'totalPl',
      }),
  },
  {
    id: 12,
    exists: async ({ database }) =>
      await tableExists({ database, tableName: 'lockedPeriodAction' }),
  },
  {
    id: 13,
    exists: async ({ database }) =>
      await tableExists({ database, tableName: 'historyFilterPreset' }),
  },
]

/**
 * 0014の全schema要素が存在するか、部分適用されているか確認する。
 * @param database migration対象のDB接続。
 * @returns 全要素の存在状態。
 */
const getMigration0014State = async ({
  database,
}: {
  readonly database: DatabaseWithTransaction['database']
}): Promise<'absent' | 'complete' | 'partial'> => {
  const results = await Promise.all([
    tableExists({ database, tableName: 'assistantProposal' }),
    tableExists({ database, tableName: 'assistantProposalAction' }),
    tableExists({ database, tableName: 'assistantRejectedFingerprint' }),
    columnExists({
      database,
      tableName: 'actionSource',
      columnName: 'tokenTransferCount',
    }),
    columnExists({
      database,
      tableName: 'actionSource',
      columnName: 'tokenTransferCountVersion',
    }),
  ])
  const existingCount = results.filter(Boolean).length
  if (existingCount === 0) {
    return 'absent'
  }
  if (existingCount === results.length) {
    return 'complete'
  }
  return 'partial'
}

/**
 * migration管理tableを持たない既存DBから適用済みbrowser migration IDを復元する。
 * migration固有のschema要素が先頭から連続していることを要求し、部分適用DBは破損として停止する。
 * @param database migration対象のDB接続。
 * @returns 復元すべき最終migration ID。
 * @throws schema世代が連続していない場合、または0014が部分適用されている場合。
 */
export const detectLegacyMigrationId = async ({
  database,
}: {
  readonly database: DatabaseWithTransaction['database']
}): Promise<number> => {
  const signatureResults: boolean[] = []
  for (const signature of legacyMigrationSignatures) {
    signatureResults.push(await signature.exists({ database }))
  }

  let lastMigrationId = -1
  let missingFound = false
  for (const [index, exists] of signatureResults.entries()) {
    if (!exists) {
      missingFound = true
      continue
    }
    if (missingFound) {
      throw new CorruptedDatabaseFileException(
        `non-contiguous legacy migration schema at ID ${index}`,
      )
    }
    lastMigrationId = index
  }

  if (lastMigrationId < 0) {
    throw new CorruptedDatabaseFileException(
      'legacy portfolio schema signature was not found',
    )
  }
  if (lastMigrationId < 13) {
    return lastMigrationId
  }

  const accountApiPatched = await hasPatchedAccountApiForeignKeys({ database })
  if (accountApiPatched) {
    lastMigrationId = 14
  }

  const migration0014State = await getMigration0014State({ database })
  if (migration0014State === 'partial') {
    throw new CorruptedDatabaseFileException(
      'migration 0014 is partially applied',
    )
  }
  if (migration0014State === 'complete') {
    if (!accountApiPatched) {
      throw new CorruptedDatabaseFileException(
        'migration 0014 exists before 0013Patch1',
      )
    }
    lastMigrationId = 15
    const migration0015Exists = parseExists({
      rows: await database.values(sql`
        SELECT EXISTS(
          SELECT 1
          FROM sqlite_master
          WHERE type = 'index'
            AND name = 'idx_assistantProposal_rule_cursor'
        );
      `),
    })
    if (migration0015Exists) {
      lastMigrationId = 16
    }
  }

  return lastMigrationId
}
